import { useInitialMonth, orderMonthOffset } from './usePinnedOrderMonth.js';
/**
 * Наряд / сводный отчёт.
 *
 * Отчёт — СНИМОК на момент сохранения (`02-DATA-MODEL.md`): строки уходят
 * в базу текстом, с именем врача и названием конструкции, а не ссылками.
 * Распечатка, отданная клинике, не должна «поехать» задним числом из-за
 * правки заказа.
 *
 * Отсюда же следует смысл режима редактирования: значения правятся ТОЛЬКО
 * в отчёте и не трогают заказы. Техник поправил формулировку конструкции для
 * конкретной клиники — заказ остался прежним.
 *
 * Два режима отбора:
 *   - по выбранным заказам (когда окно открыто из выделения в списке);
 *   - по клинике и периоду — месяц или произвольный интервал.
 *
 * Сам лист с правкой — общий компонент `ReportSheet`: тот же лист правится и
 * в сохранённом отчёте в «Документах».
 */

import { useMemo, useState } from 'react';
import { ChevronLeft, ChevronRight, FileText, FileDown, Printer, Send } from 'lucide-react';
import { SendReportDialog } from '../components/SendDialogs.js';
import {
  buildReport,
  constructionText,
  nameIndex,
  renderReportPage,
  formatDate,
  formatKop,
  formatMonthKey,
  isFirstMonth,
  monthKeyForOffset,
  todayIso,
  compareReportNames,
  itemsTotalKop,
  orderBreakdown,
  type ReportItem,
} from '@otc/shared';
import { api, downloadReport } from '../api/client.js';
import { useFacsimile, type ApiOrder, type Bootstrap } from '../api/queries.js';
import { Button, Modal } from '../components/ui.js';
import { printHtml } from '../components/printHtml.js';
import { isNarrow } from '../components/isNarrow.js';
import {
  ReportColumnsControls,
  ReportEditToggle,
  ReportSheet,
  type SheetColumns,
  type SheetGroup,
} from '../components/ReportSheet.js';

interface RowOverride {
  patient?: string;
  construction?: string;
  costKop?: number;
  /** Даты — строкой `YYYY-MM-DD`; пустой срок означает «не задан». */
  received?: string;
  due?: string;
}

export function SummaryReportModal({
  orders,
  bootstrap,
  presetOrderIds,
  onClose,
  onSaved,
  onNotify,
}: {
  orders: ApiOrder[];
  bootstrap: Bootstrap;
  /** Заказы, выделенные в списке. `null` — окно открыто без выделения. */
  presetOrderIds: string[] | null;
  onClose: () => void;
  onSaved: (message: string) => void;
  onNotify: (message: string, tone?: 'info' | 'error') => void;
}) {
  const today = todayIso();
  const initialMonth = useInitialMonth(today);

  const [mode, setMode] = useState<'preset' | 'filter'>(presetOrderIds ? 'preset' : 'filter');
  const [editMode, setEditMode] = useState(false);
  /** Телефон: там лист в правке не ужимается, а листается вбок. */
  const узкий = isNarrow();
  const facsimile = useFacsimile();
  const [busy, setBusy] = useState(false);

  /**
   * Колонки и вид наряда. Развёрнутый наряд (работа по элементам с ценами)
   * нужен не всем клиникам — Медалист принимает наряды только так, — поэтому
   * это галочка, а не вид по умолчанию.
   */
  const [columns, setColumns] = useState<SheetColumns>({
    showReceived: false,
    showDue: true,
    showSubtotal: false,
    detailed: false,
    facsimile: false,
  });
  const { detailed } = columns;

  const [clinicId, setClinicId] = useState('');
  const [periodType, setPeriodType] = useState<'month' | 'range'>('month');
  const [monthOffset, setMonthOffset] = useState(() => orderMonthOffset(today, initialMonth));
  const [rangeFrom, setRangeFrom] = useState(`${today.slice(0, 8)}01`);
  const [rangeTo, setRangeTo] = useState(today);

  const [responsible, setResponsible] = useState(
    (bootstrap.settings?.performer?.signer as string | undefined) ?? '',
  );
  /*
   * Дата наряда — по умолчанию сегодняшняя, но правится.
   *
   * Наряд часто оформляют задним числом: работу сдали в конце месяца, а
   * бумагу собирают в начале следующего. Датой на листе должна стоять та,
   * которой наряд закрывает работу, а не день, когда нажали «Печать».
   */
  const [reportDateIso, setReportDateIso] = useState(today);
  const [rowOverrides, setRowOverrides] = useState<Record<string, RowOverride>>({});
  /** Элементы, поправленные вручную: по заказу — весь его список целиком. */
  const [itemOverrides, setItemOverrides] = useState<Record<string, ReportItem[]>>({});
  const [clinicNameOverride, setClinicNameOverride] = useState<string | null>(null);
  const [legalNameOverride, setLegalNameOverride] = useState<string | null>(null);
  const [totalOverride, setTotalOverride] = useState<number | null>(null);

  // --- Отбор заказов ---------------------------------------------------------

  const monthKey = monthKeyForOffset(today, monthOffset);
  const period =
    periodType === 'month'
      ? { start: `${monthKey}-01`, end: `${monthKey}-31`, label: `${formatMonthKey(monthKey)} г.` }
      : { start: rangeFrom, end: rangeTo, label: `${formatDate(rangeFrom)} – ${formatDate(rangeTo)}` };

  const selected = useMemo(() => {
    /*
      Замороженные заказы в наряд НЕ ПОПАДАЮТ.

      Наряд — перечень работ, отданных клинике за период, и по нему сверяют
      суммы. Заказ, который врач остановил, не сделан и не оплачивается: в
      журнале и финансах он считается нулём, а строкой с ценой в наряде
      обещал бы клинике работу, которой нет.

      Правило действует и на заказы, выбранные вручную: иначе итог наряда
      разошёлся бы с итогом той же выборки в журнале.
    */
    const живые = orders.filter((order) => order.status !== 'Ожидание');

    if (mode === 'preset' && presetOrderIds) {
      return живые.filter((order) => presetOrderIds.includes(order.id));
    }
    return живые.filter((order) => {
      if (clinicId && order.clinicId !== clinicId) return false;
      return order.received >= period.start && order.received <= period.end;
    });
  }, [mode, presetOrderIds, orders, clinicId, period.start, period.end]);

  /** Сколько отложенных заказов не вошло — чтобы итог не расходился молча. */
  const frozenSkipped = useMemo(() => {
    const в = orders.filter((order) => order.status === 'Ожидание');
    if (mode === 'preset' && presetOrderIds) {
      return в.filter((order) => presetOrderIds.includes(order.id)).length;
    }
    return в.filter(
      (order) =>
        (!clinicId || order.clinicId === clinicId) &&
        order.received >= period.start &&
        order.received <= period.end,
    ).length;
  }, [mode, presetOrderIds, orders, clinicId, period.start, period.end]);

  // --- Значения строк с учётом правок ----------------------------------------

  /*
   * Врача в заказе может не быть: работу привезли из клиники, а кто её ведёт,
   * ещё не выяснили. В отчёте такие заказы собираются в отдельную группу с
   * честной подписью — безымянная группа выглядела бы сбоем вёрстки.
   */
  const doctorName = (id: string) =>
    id === '' ? 'Врач не указан' : (bootstrap.doctors.find((d) => d.id === id)?.name ?? '');
  /*
   * Конструкция в отчёте — ПОЛНОЙ СТРОКОЙ, как в журнале заказов: с
   * количеством, челюстью и доп. элементами. Собирается той же общей функцией,
   * что и письма врачу, — чтобы отчёт, письмо и журнал называли работу одинаково.
   */
  const каталог = useMemo(() => nameIndex(bootstrap.constructions), [bootstrap.constructions]);

  const patientOf = (order: ApiOrder) => rowOverrides[order.id]?.patient ?? (order.patient || '—');
  /* Даты приёмки и сдачи — как в заказе, если их не поправили в отчёте. */
  const receivedOf = (order: ApiOrder) => rowOverrides[order.id]?.received ?? order.received;
  const dueOf = (order: ApiOrder) => rowOverrides[order.id]?.due ?? (order.due ?? '');
  const constructionOf = (order: ApiOrder) =>
    rowOverrides[order.id]?.construction ?? constructionText(order, каталог);
  /** Справочник с ценами — для расшифровки работы по элементам. */
  const ценовойКаталог = useMemo(
    () => new Map(bootstrap.constructions.map((k) => [k.id, k])),
    [bootstrap.constructions],
  );

  /*
   * Элементы заказа: поправленные вручную — или разложенные по прайсу.
   *
   * Цены частей в заказе не хранятся, поэтому раскладка идёт по текущему
   * прайсу и по способу оплаты заказа, а разница с настоящей суммой заказа
   * выносится строкой «Корректировка». Способ оплаты у заказа-заготовки может
   * быть не выбран — тогда цены берутся договорные, как и при его оформлении.
   */
  const itemsOf = (order: ApiOrder): ReportItem[] =>
    itemOverrides[order.id] ?? orderBreakdown(order, ценовойКаталог, order.pay || 'По договору');

  /*
   * Сумма заказа в развёрнутом наряде — ВСЕГДА сумма его элементов: после
   * ручной правки цены элемента итог заказа и общий итог сходятся сами.
   */
  const costOf = (order: ApiOrder) =>
    detailed ? itemsTotalKop(itemsOf(order)) : (rowOverrides[order.id]?.costKop ?? order.costKop);

  const setOverride = (orderId: string, patch: RowOverride) =>
    setRowOverrides((current) => ({ ...current, [orderId]: { ...current[orderId], ...patch } }));

  /** Группировка по врачам: врачи и пациенты по алфавиту — тем же правилом, что в PDF и Excel. */
  const groups = useMemo(() => {
    const map = new Map<string, ApiOrder[]>();
    for (const order of selected) {
      const list = map.get(order.doctorId) ?? [];
      list.push(order);
      map.set(order.doctorId, list);
    }
    return [...map.entries()]
      .map(([doctorId, list]) => ({
        doctorId,
        name: doctorName(doctorId),
        orders: [...list].sort((a, b) => compareReportNames(patientOf(a), patientOf(b))),
        subtotalKop: list.reduce((sum, order) => sum + costOf(order), 0),
      }))
      .sort((a, b) => compareReportNames(a.name, b.name));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selected, rowOverrides, bootstrap.doctors, detailed, itemOverrides, ценовойКаталог]);

  /** Строки для листа — уже со всеми правками. */
  const sheetGroups: SheetGroup[] = groups.map((group) => ({
    key: group.doctorId,
    name: group.name,
    subtotalKop: group.subtotalKop,
    rows: group.orders.map((order) => ({
      key: order.id,
      patient: patientOf(order),
      construction: constructionOf(order),
      received: receivedOf(order),
      due: dueOf(order),
      costKop: costOf(order),
      items: detailed ? itemsOf(order) : [],
    })),
  }));

  const computedTotalKop = selected.reduce((sum, order) => sum + costOf(order), 0);
  const totalKop = totalOverride ?? computedTotalKop;

  // --- Заголовок отчёта ------------------------------------------------------

  const headerClinic = useMemo(() => {
    if (mode === 'preset') {
      const ids = new Set(selected.map((o) => o.clinicId));
      return ids.size === 1 ? bootstrap.clinics.find((c) => c.id === [...ids][0]) : undefined;
    }
    return clinicId ? bootstrap.clinics.find((c) => c.id === clinicId) : undefined;
  }, [mode, selected, clinicId, bootstrap.clinics]);

  /**
   * Юрлицо в шапке — по ЗАКАЗАМ отчёта, а не первое у клиники.
   *
   * У четырёх клиник юрлиц по два: «32 Белых» работает и через «Дентал Групп»,
   * «Альдента» — через «ЛОТОС» и «УСПЕХ». Раньше в шапку шло первое в списке,
   * и половина нарядов уходила с чужим юрлицом в заголовке.
   *
   * Если заказы отчёта принадлежат разным юрлицам, не подставляется ничего:
   * пустое поле правится руками, а неверное выглядит достоверно и потому
   * опаснее.
   */
  const headerLegal = useMemo(() => {
    if (!headerClinic) return undefined;
    const ids = new Set(
      selected
        .map((order) => headerClinic.branches.find((b) => b.id === order.branchId)?.legalId)
        .filter(Boolean),
    );
    if (ids.size === 1) return headerClinic.legals.find((l) => l.id === [...ids][0]);
    // Одно юрлицо у клиники — выбирать не из чего, и оно верное.
    return headerClinic.legals.length === 1 ? headerClinic.legals[0] : undefined;
  }, [headerClinic, selected]);
  const defaultClinicName =
    headerClinic?.name ?? (mode === 'preset' ? 'Выбранные заказы (разные клиники)' : 'Все клиники');
  const defaultLegalName = headerLegal
    ? headerLegal.shortForm
      ? `${headerLegal.shortForm} «${headerLegal.name}»`
      : headerLegal.name
    : '';

  const clinicName = clinicNameOverride ?? defaultClinicName;
  const legalName = legalNameOverride ?? defaultLegalName;
  const periodLabel = mode === 'preset' ? formatDate(today) : period.label;

  /** Тело запроса — одинаковое для сохранения и для выгрузки. */
  const payload = () => ({
    clinicName,
    legalName,
    periodLabel,
    responsible,
    ...columns,
    date: reportDateIso,
    rows: selected.map((order) => ({
      orderId: order.id,
      doctor: doctorName(order.doctorId),
      patient: patientOf(order),
      construction: constructionOf(order),
      received: receivedOf(order),
      // В снимке отчёта дата хранится строкой: пустая означает «срок не задан»,
      // и в печатной форме превращается в прочерк.
      due: dueOf(order),
      costKop: costOf(order),
      items: detailed ? itemsOf(order) : undefined,
    })),
  });

  const saveReport = async () => {
    setBusy(true);
    try {
      await api.post('/api/reports', payload());
      onSaved('Отчёт сохранён — смотрите в «Документы → Отчёты»');
    } catch (error) {
      onNotify(error instanceof Error ? error.message : 'Не удалось сохранить отчёт', 'error');
      setBusy(false);
    }
  };

  const download = async (format: 'xlsx' | 'pdf') => {
    setBusy(true);
    try {
      await downloadReport(payload(), format);
      onNotify(format === 'pdf' ? 'PDF скачан' : 'Файл Excel скачан');
    } catch (error) {
      onNotify(error instanceof Error ? error.message : 'Не удалось выгрузить', 'error');
    } finally {
      setBusy(false);
    }
  };

  /** Окно выбора получателей. */
  const [отправка, setОтправка] = useState(false);

  return (
    <>
    <Modal
      title="Сводный отчёт"
      onClose={onClose}
      /*
        Окно ШИРОКОЕ. В узком (48rem) при правке поля с названиями конструкций
        и элементов обрезались, и править приходилось вслепую. На телефоне
        ширина всё равно ограничена экраном.
      */
      size="2xl"
      footer={
        <>
          <Button onClick={() => void saveReport()} disabled={busy || selected.length === 0}>
            Сохранить в «Документы»
          </Button>
          {/*
            Печать собирает наряд ТЕМ ЖЕ кодом, что и сервер для PDF: тот же
            `buildReport`, та же разметка. Значит распечатанное с экрана и
            скачанный файл — один лист, а не два похожих.
          */}
          <Button
            variant="secondary"
            onClick={() => printHtml(renderReportPage(buildReport({ ...payload(), facsimileImages: facsimile })))}
            disabled={busy || selected.length === 0}
          >
            <Printer size={14} /> Печать
          </Button>
          <Button variant="secondary" onClick={() => void download('pdf')} disabled={busy || selected.length === 0}>
            <FileDown size={14} /> Скачать PDF
          </Button>
          <Button variant="secondary" onClick={() => void download('xlsx')} disabled={busy || selected.length === 0}>
            <FileText size={14} /> Скачать Excel
          </Button>
          {/* Тот же наряд, что на экране, — письмом врачу или в клинику. */}
          <Button
            variant="secondary"
            onClick={() => setОтправка(true)}
            disabled={busy || selected.length === 0}
          >
            <Send size={14} /> Отправить
          </Button>
          <Button variant="ghost" onClick={onClose}>Закрыть</Button>
          <span className="ml-auto self-center text-sm text-ink2">
            Строк: {selected.length} · <span className="tabular text-ink">{formatKop(totalKop)} ₽</span>
            {/*
              Отложенные названы прямо: иначе итог наряда молча расходился бы
              с тем, что человек видел в журнале.
            */}
            {frozenSkipped > 0 && (
              <span className="ml-2 text-[11px]" style={{ color: 'var(--otc-ink2)' }}>
                · {frozenSkipped} в ожидании не включены
              </span>
            )}
          </span>
        </>
      }
    >
      <ReportEditToggle
        editMode={editMode}
        onToggle={() => setEditMode((v) => !v)}
        narrow={узкий}
        note="Правки применяются только к отчёту и не меняют заказы"
      />

      {presetOrderIds && (
        <div className="mb-2 flex flex-wrap items-center gap-1.5 text-xs text-ink2">
          {mode === 'preset' ? (
            <>
              По {presetOrderIds.length} выбранным заказам.
              <button onClick={() => setMode('filter')} className="underline" style={{ color: 'var(--otc-accent)' }}>
                Задать период и клинику вместо этого
              </button>
            </>
          ) : (
            <>
              <button onClick={() => setMode('preset')} className="underline" style={{ color: 'var(--otc-accent)' }}>
                Вернуться к {presetOrderIds.length} выбранным заказам
              </button>
              вместо периода и клиники.
            </>
          )}
        </div>
      )}

      <ReportColumnsControls
        columns={columns}
        onChange={(patch) => setColumns((c) => ({ ...c, ...patch }))}
        facsimileAvailable={Boolean(facsimile)}
      />

      {mode === 'filter' && (
        <div className="mb-3 flex flex-wrap items-center gap-2">
          <select
            className="rounded-md border px-2.5 py-1.5 text-sm"
            style={{ borderColor: 'var(--otc-line)', background: 'var(--otc-surface)' }}
            value={clinicId}
            onChange={(e) => setClinicId(e.target.value)}
          >
            <option value="">Все клиники</option>
            {bootstrap.clinics.map((clinic) => (
              <option key={clinic.id} value={clinic.id}>{clinic.name}</option>
            ))}
          </select>

          <div className="flex overflow-hidden rounded-lg border" style={{ borderColor: 'var(--otc-line)' }}>
            {(['month', 'range'] as const).map((type) => (
              <button
                key={type}
                onClick={() => setPeriodType(type)}
                className="px-3 py-1.5 text-xs"
                style={{
                  background: periodType === type ? 'var(--otc-accent-dark)' : 'transparent',
                  color: periodType === type ? '#fff' : 'var(--otc-ink2)',
                }}
              >
                {type === 'month' ? 'Месяц' : 'Интервал'}
              </button>
            ))}
          </div>

          {periodType === 'month' ? (
            <div className="flex items-center gap-1.5">
              <button
                onClick={() => setMonthOffset((m) => m - 1)}
                /* Отчёт за 2025-й пуст: данные начинаются с января 2026. */
                disabled={isFirstMonth(monthKey)}
                className="rounded-md border p-1 disabled:opacity-40"
                style={{ borderColor: 'var(--otc-line)' }}
              >
                <ChevronLeft size={14} className="text-ink2" />
              </button>
              <span className="w-36 text-center text-sm text-ink first-letter:uppercase">{formatMonthKey(monthKey)}</span>
              <button
                onClick={() => setMonthOffset((m) => m + 1)}
                className="rounded-md border p-1"
                style={{ borderColor: 'var(--otc-line)' }}
              >
                <ChevronRight size={14} className="text-ink2" />
              </button>
            </div>
          ) : (
            <div className="flex items-center gap-1.5 text-sm">
              <input
                type="date"
                className="rounded-md border px-2 py-1 text-xs"
                style={{ borderColor: 'var(--otc-line)' }}
                value={rangeFrom}
                onChange={(e) => setRangeFrom(e.target.value)}
              />
              <span className="text-ink2">—</span>
              <input
                type="date"
                className="rounded-md border px-2 py-1 text-xs"
                style={{ borderColor: 'var(--otc-line)' }}
                value={rangeTo}
                onChange={(e) => setRangeTo(e.target.value)}
              />
            </div>
          )}
        </div>
      )}

      <ReportSheet
        editMode={editMode}
        narrow={узкий}
        clinicName={clinicName}
        legalName={legalName}
        dateIso={reportDateIso}
        responsible={responsible}
        onHeader={(patch) => {
          if (patch.clinicName !== undefined) setClinicNameOverride(patch.clinicName);
          if (patch.legalName !== undefined) setLegalNameOverride(patch.legalName);
          if (patch.dateIso !== undefined) setReportDateIso(patch.dateIso || today);
          if (patch.responsible !== undefined) setResponsible(patch.responsible);
        }}
        columns={columns}
        groups={sheetGroups}
        totalKop={totalKop}
        onRow={(key, patch) => setOverride(key, patch)}
        onItems={(key, items) => setItemOverrides((current) => ({ ...current, [key]: items }))}
        onTotalChange={(kop) => setTotalOverride(kop)}
        onTotalReset={
          totalOverride !== null && totalOverride !== computedTotalKop ? () => setTotalOverride(null) : undefined
        }
        emptyText="Нет заказов за выбранный период"
        facsimileImages={facsimile}
      />
    </Modal>

    {/* Получатели и письмо: тот же наряд уходит вложением в PDF. */}
    {отправка && (
      <SendReportDialog
        report={payload()}
        onClose={() => setОтправка(false)}
        onDone={(текст) => {
          setОтправка(false);
          onNotify(текст);
        }}
      />
    )}
    </>
  );
}
